import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../LanguageContext';
import { FaBuilding, FaCalendar, FaCheckCircle, FaGlobe } from 'react-icons/fa';

const About = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.6 },
    },
  };

  const keyInfo = [
    { icon: FaCalendar, label: t.about.established, value: t.about.establishedDate },
    { icon: FaBuilding, label: t.about.legalForm, value: t.about.legalFormValue },
    { icon: FaCheckCircle, label: t.about.status, value: t.about.statusValue },
    { icon: FaGlobe, label: t.about.jurisdiction, value: t.about.jurisdictionValue },
  ];

  return (
    <section
      id="about"
      ref={ref}
      className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
      data-testid="about-section"
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-solar-200/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-energy-200/20 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gradient" data-testid="about-title">
            {t.about.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-solar-400 to-energy-500 mx-auto rounded-full" />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img
                src="/images/about.jpg"
                alt="Solar Installation"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              
              {/* Floating badge */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute bottom-8 left-8 bg-white rounded-xl p-4 shadow-xl"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-solar-400 to-energy-500 rounded-full flex items-center justify-center">
                    <FaCheckCircle className="text-white text-2xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{t.about.status}</p>
                    <p className="font-bold text-solar-600">{t.about.statusValue}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Content side */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="space-y-6"
          >
            <motion.p variants={itemVariants} className="text-lg text-gray-700 leading-relaxed">
              {t.about.description1}
            </motion.p>
            <motion.p variants={itemVariants} className="text-lg text-gray-700 leading-relaxed">
              {t.about.description2}
            </motion.p>
            <motion.p variants={itemVariants} className="text-lg text-gray-700 leading-relaxed">
              {t.about.description3}
            </motion.p>

            <motion.div variants={itemVariants} className="pt-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">{t.about.keyInfo}</h3>
              <div className="grid grid-cols-1 gap-4">
                {keyInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.02, x: 5 }}
                    className="flex items-start space-x-4 bg-white p-4 rounded-xl shadow-md border border-gray-100"
                    data-testid={`key-info-${index}`}
                  >
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-solar-400 to-energy-500 rounded-lg flex items-center justify-center">
                        <info.icon className="text-white text-lg" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-500">{info.label}</p>
                      <p className="text-base font-medium text-gray-800">{info.value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;